import { Icon as BlueIcon } from '@blueprintjs/core';

import { WindowButton } from '@components/Filters/FilterProduct/components/FilterProductContent/FilterProductContent.styles';

import ProductDefault from '@assets/png/product.png';
import CheckboxChecked from '@assets/svg/checkbox-checked.svg';

import { useContext } from 'react';
import { AssetContext } from '@pages/Asset/Asset.query';
import ReactTooltip from 'react-tooltip';

import * as yup from 'yup';
import { useMutation, useQuery } from 'react-query';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { cloneAssets } from '@services/queries/equipments/cloneAssets/cloneAssets';
import { toast } from 'react-toast';
import { indexUniqueFields } from '@services/queries/customField/indexUniqueFields/indexUniqueFields';

import { StateMethods } from '@hookstate/core';
import {
  Container,
  Head,
  Title,
  Product,
  ProductImage,
  ProductTitle,
  ProductRight,
  ProductModel,
  InfoContainer,
  SectionTitle,
  InfoRight,
  Checkbox,
  ItemsList,
  ItemRow,
  ItemName,
  Line,
  Label,
  Input,
  ButtonContainer,
  Button,
  HelpToolTipTextDiv,
  Form,
  InputsWrapper,
  Wrapper,
  OverflowDiv,
} from './CloneAssetMenu.styles';

interface ICloneAssetMenu {
  onClose: () => void;
  openCloneAsset: StateMethods<boolean>;
}

interface IFormData {
  id: string | undefined;
  clone: {
    status: boolean;
    assignee: boolean;
    tags: boolean;
    photos: boolean;
    internalData: boolean;
  };
  serial: string;
  customFields: {
    id: string;
    value: string;
  }[];
}

const CloneAssetMenu = ({ onClose, openCloneAsset }: ICloneAssetMenu) => {
  const { equipmentQuery } = useContext(AssetContext);

  const schema = yup.object().shape({
    clone: yup.object().shape({
      status: yup.boolean().required(),
      assignee: yup.boolean().required(),
      tags: yup.boolean().required(),
      photos: yup.boolean().required(),
      internalData: yup.boolean().required(),
    }),
    serial: yup.string().required(),
    customFields: yup.array().of(
      yup.object().shape({
        id: yup.string(),
        value: yup.string(),
      }),
    ),
  });

  const equipment = equipmentQuery?.data;
  const { product } = equipment || {};

  const {
    register,
    handleSubmit,
    formState: { isValid },
    reset,
    control,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      clone: {
        assignee: true,
        internalData: true,
        photos: true,
        status: true,
        tags: true,
      },
    },
  });

  const { fields, replace } = useFieldArray({
    name: 'customFields',
    control,
    keyName: 'formId',
  });

  const { data } = useQuery(
    ['uniqueFields'],
    () => indexUniqueFields({ data: { isUnique: true } }),
    {
      onSuccess: successData => {
        const dataMapped = successData.map(item => ({
          id: item.id,
          value: '',
        }));
        const fieldsNotMapped = dataMapped.filter(field1 => {
          return !fields.find(field2 => field2.id === field1.id);
        });
        const fieldsMerged = [...fields, ...fieldsNotMapped];
        replace(fieldsMerged);
      },
    },
  );

  const mutationCloneAsset = useMutation(cloneAssets, {
    onSuccess: () => {
      toast.success(`Asset cloned successfully`);
      openCloneAsset.set(false);
      reset();
    },
  });

  const onSubmit = (formData: IFormData) => {
    if (!mutationCloneAsset.isLoading) {
      mutationCloneAsset.mutate({
        id: equipment?.id,
        clone: {
          status: formData.clone.status,
          assignee: formData.clone.assignee,
          tags: formData.clone.tags,
          photos: formData.clone.photos,
          internalData: formData.clone.internalData,
        },
        customFields: formData.customFields,
        serial: formData.serial,
      });
    }
  };

  return (
    <Container>
      <Head style={{ marginBottom: 16 }}>
        <Title>Clone Asset</Title>

        <div style={{ display: 'flex', flexDirection: 'row' }}>
          <WindowButton
            onClick={() => {
              onClose();
            }}
          >
            <BlueIcon icon="cross" color="#8F95B2" />
          </WindowButton>
        </div>
      </Head>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <OverflowDiv>
          <Product>
            <ProductImage src={product?.imageUrl || ProductDefault} />
            <ProductRight>
              <ProductTitle>{equipment?.productName}</ProductTitle>
              <ProductModel>{equipment?.productModel}</ProductModel>
            </ProductRight>
          </Product>

          <InfoContainer>
            <SectionTitle>Select the items you want to be copied</SectionTitle>
            <InfoRight data-tip data-for="helpTooltipCloneAsset">
              <BlueIcon icon="info-sign" color="#8F95B2" />
            </InfoRight>

            <ReactTooltip
              className="tooltip"
              id="helpTooltipCloneAsset"
              effect="solid"
              multiline
              textColor="#fff"
              backgroundColor="#161A30"
            >
              <HelpToolTipTextDiv>
                Name, Photo, Model, Manufacturer, Product Data, Updates,
                Contacts, Resources and Documents are product related fields and
                will always be copied by default.
              </HelpToolTipTextDiv>
            </ReactTooltip>
          </InfoContainer>

          <ItemsList>
            <ItemRow>
              <Controller
                control={control}
                name="clone.status"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    checked={value}
                    onClick={() => onChange(!value)}
                    type="button"
                  >
                    {value ? (
                      <img
                        src={CheckboxChecked}
                        alt="Checked"
                        width="16px"
                        height="16px"
                      />
                    ) : (
                      ''
                    )}
                  </Checkbox>
                )}
              />

              <ItemName>Status</ItemName>
            </ItemRow>
            <ItemRow>
              <Controller
                control={control}
                name="clone.tags"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    checked={value}
                    onClick={() => onChange(!value)}
                    type="button"
                  >
                    {value ? (
                      <img
                        src={CheckboxChecked}
                        alt="Checked"
                        width="16px"
                        height="16px"
                      />
                    ) : (
                      ''
                    )}
                  </Checkbox>
                )}
              />
              <ItemName>Tags</ItemName>
            </ItemRow>
            <ItemRow>
              <Controller
                control={control}
                name="clone.assignee"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    checked={value}
                    onClick={() => onChange(!value)}
                    type="button"
                  >
                    {value ? (
                      <img
                        src={CheckboxChecked}
                        alt="Checked"
                        width="16px"
                        height="16px"
                      />
                    ) : (
                      ''
                    )}
                  </Checkbox>
                )}
              />
              <ItemName>Assignee</ItemName>
            </ItemRow>
            <ItemRow>
              <Controller
                control={control}
                name="clone.internalData"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    checked={value}
                    onClick={() => onChange(!value)}
                    type="button"
                  >
                    {value ? (
                      <img
                        src={CheckboxChecked}
                        alt="Checked"
                        width="16px"
                        height="16px"
                      />
                    ) : (
                      ''
                    )}
                  </Checkbox>
                )}
              />
              <ItemName>Internal Data</ItemName>
            </ItemRow>
            <ItemRow>
              <Controller
                control={control}
                name="clone.photos"
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    checked={value}
                    onClick={() => onChange(!value)}
                    type="button"
                  >
                    {value ? (
                      <img
                        src={CheckboxChecked}
                        alt="Checked"
                        width="16px"
                        height="16px"
                      />
                    ) : (
                      ''
                    )}
                  </Checkbox>
                )}
              />
              <ItemName>Photos</ItemName>
            </ItemRow>
          </ItemsList>

          <Line />

          <InfoContainer>
            <SectionTitle>
              The following items are unique fields and cannot be copied. You
              can provide new data below:
            </SectionTitle>
          </InfoContainer>

          <InputsWrapper>
            <Wrapper>
              <Label>New Serial #</Label>
              <Input
                placeholder="Type a new Serial #"
                {...register('serial')}
              />
            </Wrapper>

            {fields.map((field, index) => {
              const customField = data?.find(item => item.id === field.id);

              return (
                <Wrapper key={field.formId}>
                  <Label className="optional">
                    {customField?.name} (Optional)
                  </Label>
                  <Input
                    placeholder={`Type a new ${customField?.name}`}
                    {...register(`customFields.${index}.value`)}
                  />
                </Wrapper>
              );
            })}
          </InputsWrapper>
        </OverflowDiv>
        <ButtonContainer>
          <Button
            type="submit"
            disabled={!isValid || mutationCloneAsset.isLoading}
          >
            Create Clone
          </Button>
        </ButtonContainer>
      </Form>
    </Container>
  );
};

export { CloneAssetMenu };
